import './styles.scss'

import { useCallback } from 'react'
import { Col, Row } from 'antd'

import AddToCartBtn from '@/components/AddToCartBtn'
import ProductCard from '@/components/ProductCard'
import { useAppDispatch } from '@/store/hook'
import { addProductToCart } from '@/store/slices/cartSlice'
import { sendProductDetail, showAddProductToWishlistModal } from '@/store/slices/wishlistSlice'
import { useDeviceDetect } from '@/hooks'
import usePermission from '@/hooks/usePermission'
import { MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'

interface ProductCardListProps {
  products: unknown[]
  comparingProductIds?: string[]
  onCompareHandler?: (...args: unknown[]) => unknown
}

const ProductCardList = ({
  products,
  comparingProductIds,
  onCompareHandler,
}: ProductCardListProps) => {
  const dispatch = useAppDispatch()
  const { isLargeScreen } = useDeviceDetect()
  const {
    verifiedPermissions: [canViewListPrice, canAddProductToCart],
  } = usePermission([
    MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_LIST_PRICE,
    MODULE_SHOP_NAVIGATION_RIGHTS.ADD_PRODUCT_TO_SHOPPING_CART,
  ])

  const onCheckHandler = useCallback(
    (product, event) => {
      const isChecked = event?.target?.checked

      onCompareHandler?.(isChecked, product)
    },
    [onCompareHandler],
  )

  const wrapperClass = isLargeScreen
    ? 'product-card-list__wrapper'
    : 'full-width-page-component__wrapper'

  const onAddProductToCart = useCallback(
    (materialNumber) => {
      dispatch(addProductToCart({ materialNumber }))
    },
    [dispatch],
  )

  const handleAddToWishlist = ({ materialName, materialNumber, img }) => {
    dispatch(sendProductDetail({ materialName, materialNumber, img }))
    dispatch(showAddProductToWishlistModal())
  }

  return (
    <div className={wrapperClass}>
      <Row
        className="product-card-list__container"
        gutter={[
          { xs: 0, sm: 0, md: 24, lg: 32, xl: 32, xxl: 32 },
          { xs: 16, sm: 16, md: 24, lg: 32, xl: 32, xxl: 32 },
        ]}
      >
        {products.map((product) => (
          <Col
            key={product.materialNumber}
            lg={7}
            md={12}
            sm={24}
            xl={8}
            xs={24}
            xxl={6}
          >
            <ProductCard
              isCompareCheckboxDisplayed
              category={product.categoryName}
              comparingProductIds={comparingProductIds}
              currencyUnit={product?.currencyUnit}
              img={product.pictureUrl}
              listPrice={product.listPrice}
              materialNumber={product.materialNumber}
              name={product.materialName}
              requiresInstruction={product.requiresInstruction}
              serialNo={product.serialNo}
              addToCartBtn={
                <AddToCartBtn
                  handleClick={() => onAddProductToCart(product.materialNumber)}
                />
              }
              displayAddToCart={product?.displayAddToCart && canAddProductToCart}
              shouldDisplayListPrice={product?.displayListPrice && canViewListPrice}
              onAddToWishlist={handleAddToWishlist}
              onCheckHandler={onCheckHandler}
            />
          </Col>
        ))}
      </Row>
    </div>
  )
}

export default ProductCardList
